import AddIcon from '@mui/icons-material/Add';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import TextSnippetRoundedIcon from '@mui/icons-material/TextSnippetRounded';
import { Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { getRoutePath } from 'config/routes';
import { useSidebarStore } from 'features/pageSidebar/useSidebarStore';
import { MouseEvent, SyntheticEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import gtmIds from 'services/tracking/GTMIds';
import { withGtmInteraction } from 'services/tracking/withGtmInteraction';
import styled from 'styled-components';
import { useMenu } from 'utils/hooks/useMenu';

export const CreateButton = () => {
  const isSidebarExpanded = useSidebarStore(state => state.isExpanded);

  const { anchorEl, isOpen, onTriggerClick, onMenuClose } = useMenu();

  const navigate = useNavigate();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onTriggerClick(event);
  };

  const handleMenuClose = (event: SyntheticEvent) => {
    event.stopPropagation();
    onMenuClose();
  };

  const handleNewDocumentClick = () => {
    navigate(getRoutePath('newDocument'));
    onMenuClose();
  };

  const handleNewImageClick = () => {
    navigate(getRoutePath('aiImages'));
    onMenuClose();
  };

  return (
    <>
      <StyledButtonContainer>
        {!isSidebarExpanded ? (
          <StyledIconButton
            {...withGtmInteraction(gtmIds.sidebar.createButton.click)}
            size="small"
            onClick={handleClick}
            color="inherit"
          >
            <AddIcon />
          </StyledIconButton>
        ) : (
          <Button
            {...withGtmInteraction(gtmIds.sidebar.createButton.click)}
            variant="contained"
            fullWidth
            startIcon={<AddIcon />}
            onClick={handleClick}
          >
            <FormattedMessage id="common.create" />
          </Button>
        )}
      </StyledButtonContainer>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          {...withGtmInteraction(gtmIds.sidebar.createButton.newDocument)}
          onClick={handleNewDocumentClick}
        >
          <ListItemIcon>
            <TextSnippetRoundedIcon />
          </ListItemIcon>
          <ListItemText primary={<FormattedMessage id="layout.sidebar.create.new_document" />} />
        </MenuItem>
        <MenuItem
          {...withGtmInteraction(gtmIds.sidebar.createButton.newImage)}
          onClick={handleNewImageClick}
        >
          <ListItemIcon>
            <InsertPhotoOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary={<FormattedMessage id="layout.sidebar.create.new_image" />} />
        </MenuItem>
      </Menu>
    </>
  );
};

const StyledButtonContainer = styled.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacings.four};
  color: ${({ theme }) => theme.colors.commonWhiteMain};
`;

const StyledIconButton = styled(IconButton)`
  background-color: ${({ theme }) => theme.colors.primaryColorMain};
  margin-left: ${({ theme }) => theme.spacings.two};

  &.MuiIconButton-root:hover {
    background-color: ${({ theme }) => theme.colors.primaryColorDark};
  }
`;
